"use client";

import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";

type Props = {
  paymentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestored: () => Promise<void> | void;
};

export default function RestorePaymentDialog({ paymentId, open, onOpenChange, onRestored }: Props) {
  const { t } = useTranslation(["finances", "common"]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-foreground/80 h-fit max-h-dvh max-w-xl border-none p-0 backdrop-blur-xl">
        <ScrollArea className="h-full max-h-dvh">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-4">
              <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
                <div className="bg-muted/20 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                  <Icon
                    className="text-accent-foreground"
                    height="1.25rem"
                    icon="mdi:backup-restore"
                    width="1.25rem"
                  />
                </div>
                {t("finances:paymentsDetails.deleteRestore.restoreTitle", "Restore payment")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-start gap-3">
              <div className="text-muted-foreground text-sm">
                <p>
                  {t(
                    "finances:paymentsDetails.deleteRestore.restoreDesc1",
                    "This will restore the archived payment back to your list.",
                  )}
                </p>
                <p className="mt-2">
                  {t(
                    "finances:paymentsDetails.deleteRestore.restoreDesc2",
                    "Use this when you want to bring the payment back from the archive.",
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:cancel", "Cancel")}
              </Button>
              <Button
                className="gap-2 rounded-xl"
                type="button"
                variant="foreground"
                onClick={async () => {
                  if (!paymentId) return;
                  try {
                    await axiosApiInstance.post(
                      API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_RESTORE(paymentId),
                    );
                    toast.success(
                      t(
                        "finances:paymentsDetails.deleteRestore.restoreSuccess",
                        "Payment restored",
                      ),
                    );
                    await onRestored?.();
                    onOpenChange(false);
                  } catch (err: any) {
                    const code = err?.response?.data?.error;
                    const msg =
                      (code &&
                        t(`finances:paymentsDetails.deleteRestore.restoreError.${code}` as any)) ||
                      t(
                        "finances:paymentsDetails.deleteRestore.restoreError.generic",
                        "Failed to restore payment. Please try again.",
                      );

                    toast.error(msg);
                  }
                }}
              >
                <Icon height="1rem" icon="mdi:backup-restore" width="1rem" />
                {t("finances:paymentsDetails.deleteRestore.restoreCta", "Restore payment")}
              </Button>
            </div>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
